import { colorCrosswalkConclusion, modifierKey, selectedCrosswalkSource, type ColorCrosswalkDraft } from "@/lib/manufacturing/colorCrosswalkConclusion";
import type { ColorCrosswalkField, ColorCrosswalkModifier } from "@/lib/manufacturing/colorCrosswalk";
import type { ColorCrosswalkCard } from "@/lib/manufacturing/colorCrosswalkPropose";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

const FIELD_LABEL: Record<string, string> = {
  series: "Series",
  roof_style: "Roof style",
  package: "Package",
  roof_type: "Roof type",
  roof_color: "Roof color",
  siding_type: "Siding type",
  siding_color: "Siding color",
  trim_color: "Trim color",
  addon_name: "Add-on",
};

export type FinishPick = {
  value: string;
  label: string;
  memberId: string;
};

function modifierLabel(modifier: ColorCrosswalkModifier): string {
  const name = FIELD_LABEL[modifier.field] ?? modifier.field;
  if (modifier.op === "contains") return `${name} contains ${modifier.value}`;
  return `${name} is ${modifier.value}`;
}

export function InventoryOutlookColorCrosswalkCard({
  card,
  draft,
  picks,
  series,
  busy,
  onPatch,
  onSave,
}: {
  card: ColorCrosswalkCard;
  draft: ColorCrosswalkDraft;
  picks: FinishPick[];
  series: string[];
  busy: boolean;
  onPatch: (patch: Partial<ColorCrosswalkDraft>) => void;
  onSave: () => void;
}) {
  const source = selectedCrosswalkSource(card, draft);
  const memberPick = draft.pendingMemberId ? `member:${draft.pendingMemberId}` : "";
  const pickValue = picks.some((pick) => pick.value === memberPick)
    ? memberPick
    : picks.some((pick) => pick.value === draft.finishCode)
      ? draft.finishCode
      : "";
  return (
    <li className="space-y-2 rounded-xl border border-gray-200 p-3 text-sm dark:border-zinc-700">
      <p className="font-semibold">
        {FIELD_LABEL[source.field] ?? source.field} {source.value}
      </p>
      <p className="text-gray-600 dark:text-zinc-400">
        {card.use_family.replace(/_/g, " ")} · {card.finish_channel} channel · {card.serial_count} serials · {card.use_codes.join(", ")}
      </p>
      <div className="flex flex-wrap gap-2">
        {card.modifiers.map((modifier) => (
          <label key={modifierKey(modifier)} className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700">
            <input type="checkbox" checked={draft.seriesOn} onChange={() => onPatch({ seriesOn: !draft.seriesOn })} />
            {modifierLabel(modifier)}
          </label>
        ))}
        {series.map((value) => (
          <label key={value} className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700">
            <input
              type="checkbox"
              checked={draft.extraSeries.includes(value)}
              onChange={() =>
                onPatch({
                  extraSeries: draft.extraSeries.includes(value)
                    ? draft.extraSeries.filter((item) => item !== value)
                    : [...draft.extraSeries, value],
                })
              }
            />
            Series is {value}
          </label>
        ))}
        {card.optional_modifiers.map((modifier) => {
          const key = modifierKey(modifier);
          return (
            <label key={key} className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700">
              <input
                type="checkbox"
                checked={draft.optional.includes(key)}
                onChange={() =>
                  onPatch({
                    optional: draft.optional.includes(key) ? draft.optional.filter((item) => item !== key) : [...draft.optional, key],
                  })
                }
              />
              {modifierLabel(modifier)}
            </label>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={inputClass}
          aria-label={`Order color for ${card.finish_channel}`}
          value={`${source.field}:${source.value}`}
          onChange={(e) => {
            const [field, ...rest] = e.target.value.split(":");
            onPatch({ sourceField: field as ColorCrosswalkField, sourceValue: rest.join(":") });
          }}
        >
          {card.color_choices.map((choice) => (
            <option key={`${choice.field}:${choice.value}`} value={`${choice.field}:${choice.value}`}>
              {FIELD_LABEL[choice.field] ?? choice.field} {choice.value}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          aria-label={`Finish for ${card.finish_channel}`}
          value={pickValue}
          onChange={(e) => {
            const pick = picks.find((item) => item.value === e.target.value);
            if (pick?.memberId) {
              onPatch({
                pendingMemberId: pick.memberId,
                finishCode: draft.finishCode.startsWith("member:") ? "" : draft.finishCode,
              });
              return;
            }
            onPatch({ finishCode: e.target.value, pendingMemberId: "" });
          }}
        >
          <option value="">{picks.length > 0 ? "Finish on the part" : "No member SKUs on this part use"}</option>
          {picks.map((pick) => (
            <option key={pick.value} value={pick.value}>
              {pick.label}
            </option>
          ))}
        </select>
        <input
          className={inputClass}
          value={draft.finishCode}
          placeholder="Finish code"
          aria-label={`Finish code for ${source.value}`}
          onChange={(e) => onPatch({ finishCode: e.target.value })}
        />
        <button
          type="button"
          disabled={busy || !draft.finishCode.trim()}
          className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          onClick={onSave}
        >
          Save mapping
        </button>
        <p className="min-w-[16rem] flex-1 text-gray-800 dark:text-zinc-200" aria-live="polite">
          {colorCrosswalkConclusion(card, draft)}
        </p>
      </div>
    </li>
  );
}
